import { createSelector, createSlice } from '@reduxjs/toolkit';

import { logoutUser } from '@/features/auth/authThunks';
import type { RootState } from '@/store/root-state';
import {
  buildExpenseDetailsKey,
  buildExpenseStatisticsKey,
  fetchPropertyExpenseDetails,
  fetchPropertyExpenseStatistics,
} from './propertyExpensesThunks';
import type { PropertyExpenseBucketState, PropertyExpensesState } from './propertyExpensesTypes';

const initialState: PropertyExpensesState = {
  statisticsByKey: {},
  detailsByKey: {},
};

function emptyBucket<T>(): PropertyExpenseBucketState<T> {
  return {
    loading: false,
    loaded: false,
  };
}

function ensureStatisticsBucket(
  state: PropertyExpensesState,
  key: string,
): NonNullable<PropertyExpensesState['statisticsByKey'][string]> {
  state.statisticsByKey[key] ??= emptyBucket();
  return state.statisticsByKey[key];
}

function ensureDetailsBucket(
  state: PropertyExpensesState,
  key: string,
): NonNullable<PropertyExpensesState['detailsByKey'][string]> {
  state.detailsByKey[key] ??= emptyBucket();
  return state.detailsByKey[key];
}

function readError(action: { payload?: { message?: string }; error?: { message?: string } }, fallback: string): string {
  return action.payload?.message || action.error?.message || fallback;
}

const propertyExpensesSlice = createSlice({
  name: 'propertyExpenses',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPropertyExpenseStatistics.pending, (state, action) => {
        const bucket = ensureStatisticsBucket(state, buildExpenseStatisticsKey(action.meta.arg));
        bucket.loading = true;
        bucket.error = undefined;
      })
      .addCase(fetchPropertyExpenseStatistics.fulfilled, (state, action) => {
        const bucket = ensureStatisticsBucket(state, buildExpenseStatisticsKey(action.meta.arg));
        bucket.data = action.payload;
        bucket.loading = false;
        bucket.loaded = true;
        bucket.error = undefined;
        bucket.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchPropertyExpenseStatistics.rejected, (state, action) => {
        if (action.meta.condition) return;

        const bucket = ensureStatisticsBucket(state, buildExpenseStatisticsKey(action.meta.arg));
        bucket.loading = false;
        bucket.loaded = true;
        bucket.error = readError(action, 'Failed to load property expense statistics');
      })
      .addCase(fetchPropertyExpenseDetails.pending, (state, action) => {
        const bucket = ensureDetailsBucket(state, buildExpenseDetailsKey(action.meta.arg));
        bucket.loading = true;
        bucket.error = undefined;
      })
      .addCase(fetchPropertyExpenseDetails.fulfilled, (state, action) => {
        const bucket = ensureDetailsBucket(state, buildExpenseDetailsKey(action.meta.arg));
        bucket.data = action.payload;
        bucket.loading = false;
        bucket.loaded = true;
        bucket.error = undefined;
        bucket.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchPropertyExpenseDetails.rejected, (state, action) => {
        if (action.meta.condition) return;

        const bucket = ensureDetailsBucket(state, buildExpenseDetailsKey(action.meta.arg));
        bucket.loading = false;
        bucket.loaded = true;
        bucket.error = readError(action, 'Failed to load property expense details');
      })
      .addCase(logoutUser.fulfilled, () => initialState);
  },
});

export default propertyExpensesSlice.reducer;

const selectPropertyExpensesState = (state: RootState) => state.propertyExpenses;

export const selectPropertyExpenseStatisticsBucket = createSelector(
  [selectPropertyExpensesState, (_: RootState, key: string) => key],
  (state, key) => state.statisticsByKey[key],
);

export const selectPropertyExpenseDetailsBucket = createSelector(
  [selectPropertyExpensesState, (_: RootState, key: string) => key],
  (state, key) => state.detailsByKey[key],
);
